// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

/**
 * Lets an identity provider end its own session when the app logs out.
 *
 * Without this, signing out of the app while a Clerk session is still alive
 * would make the auth sync log the user straight back in.
 */

type LogoutHook = () => void | Promise<void>;

const hooks = new Set<LogoutHook>();

/** Registers a hook; returns a function that unregisters it. */
export function registerLogoutHook(hook: LogoutHook): () => void {
  hooks.add(hook);
  return () => {
    hooks.delete(hook);
  };
}

/** Runs every hook in registration order. A failing hook never blocks the rest. */
export async function runLogoutHooks(): Promise<void> {
  for (const hook of [...hooks]) {
    try {
      await hook();
    } catch (error) {
      console.warn('[logoutHooks] A logout hook failed:', error);
    }
  }
}
